import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import ImageModule from 'docxtemplater-image-module-free';

/**
 * Generate and download the "Acta de devolución" Word document from a .docx template.
 *
 * Expects the template at public/docs/Actas asignaciones inventario/Acta de devolución.docx
 *
 * @param {Object} params
 * @param {string} [params.day] - Optional day override (defaults to today)
 * @param {string} [params.monthName] - Optional month name override (defaults to today, locale browser)
 * @param {string|number} [params.year] - Optional year override (defaults to current year)
 * @param {string} params.assignedWorker - Worker who returns the item
 * @param {string} params.responsible - User responsible for receiving/recording
 * @param {Date|string|number} params.devolutionDate - Devolution date
 * @param {Date|string|number} params.assignmentDate - Original assignment date
 * @param {string} params.buyId - Purchase ID
 * @param {string} params.buyCode - Receipt/Invoice number
 * @param {string} params.product - Product/Item name
 * @param {string} [params.brand] - Brand or supplier name
 * @param {string|number} [params.price] - Unit value
 * @param {Date|string|number} [params.buyDate] - Purchase date
 * @returns {Promise<void>}
 */
export default async function GenerateTxtFileBlockAssignment({
  day, // Día
  monthName, // Mes
  year, // Año
  assignedWorker, // Usuario asignado al bloque
  responsible, // Usuario responsable
  type, // Tipo de acta (recepción o devolución)
  blockDateStart, // Fecha de inicio del bloque
  blockDateEnd, // Fecha de fin del bloque
  items // Lista de items en el bloque, cada uno cuenta con los siguientes campos:
    // buyId,
    // buyCode,
    // product,
    // brand,
    // price,
    // buyDate,
    // urlImage,
    // image,
    // currency,x
}) {
  const templatePath = `/docs/Actas asignaciones inventario/Acta de ${type} - Bloque.docx`;

  const formatDateToDisplay = (value) => {
    if (!value) return '';
    // Accepts Date, timestamp, or string; returns dd/mm/yyyy
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = String(date.getFullYear());
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return String(value);
    }
  };

  const safe = (v, fallback = '') => (v === undefined || v === null ? fallback : v);

  // Compute header date parts if not provided
  const now = new Date();
  const headerDay = safe(day, String(now.getDate()));
  const headerMonthName = safe(
    monthName,
    now.toLocaleString('default', { month: 'long' })
  );
  const headerYear = safe(year, String(now.getFullYear()));

  // Build data object expected by the template
  const dataForTemplate = {
    // Header date fragments
    city: 'Lima',
    day: headerDay,
    monthName: headerMonthName,
    year: headerYear,

    // Flat fields to match the updated template placeholders
    assignedWorker: safe(assignedWorker, ''),
    responsible: safe(responsible, ''),
    blockDateStart: safe(formatDateToDisplay(blockDateStart), ''),
    blockDateEnd: safe(formatDateToDisplay(blockDateEnd), ''),
    items: safe(items.map((item, i) => ({
      index: safe(i + 1, ''),
      buyId: safe(item.buyId, ''),
      buyCode: safe(item.buyCode, ''),
      buyDate: safe(formatDateToDisplay(item.buyDate), ''),
      itemRegisterDate: safe(formatDateToDisplay(item.itemRegisterDate), ''),
      product: safe(item.product, ''),
      currency: safe(item.currency, ''),
      price: safe(item.price, ''),
      image: safe(item.image, ''),
    })), ''),
  };

  try {
    const response = await fetch(encodeURI(templatePath));
    if (!response.ok) {
      throw new Error(`No se pudo cargar la plantilla (${response.status})`);
    }
    const arrayBuffer = await response.arrayBuffer();

    const imageOptions = { // Imagen
      async getImage(tagValue /*, tagName */) {
        try {
          if (!tagValue) return new Uint8Array(0);
          const isDataUrl = typeof tagValue === 'string' && tagValue.startsWith('data:');
          if (isDataUrl) {
            const base64 = tagValue.split(',')[1] || '';
            const binary = atob(base64);
            const len = binary.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
            return bytes; // Uint8Array
          }
          const res = await fetch(tagValue, { cache: 'no-store', mode: 'cors' });
          if (!res.ok) throw new Error(`No se pudo obtener la imagen (${res.status})`);
          const buf = await res.arrayBuffer();
          return new Uint8Array(buf); // Uint8Array
        } catch (e) {
          return new Uint8Array(0);
        }
      },
      getSize() {
        const maxHeight = 200;
        // Width auto (approx) while preserving some ratio even if unknown
        const width = 300;
        return [width, maxHeight];
      },
    };

    const zip = new PizZip(arrayBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      nullGetter: () => '',
      modules: [new ImageModule(imageOptions)],
    });

    await doc.renderAsync(dataForTemplate);

    const out = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const normalizePart = (text) => String(text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove accents
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .toLowerCase();

    const fileTypePart = normalizePart(type || 'documento');
    const workerPart = dataForTemplate.assignedWorker ? `_${normalizePart(dataForTemplate.assignedWorker)}` : '';
    const fileName = `Acta_de_${fileTypePart}${workerPart}.docx`;
    saveAs(out, fileName);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error generando el acta de ${type}: ` + error);
    throw error;
  }
}